// Text slides: the free, instant, deterministic path — a volunteer picks a
// template, fills in a few boxes, and gets a Liberty Bible Church slide. No AI
// call, no cost, and the same result every time.
//
// A slide is stored as JSON in `content.data`, so adding fields needs no
// migration. Everything here is optional and every reader defaults, which is
// what keeps older slides (headline + subtext + two colours, no layout) working
// untouched.

/** How the fields are arranged. `centered` is the original behaviour and stays
 *  the default for anything without an explicit layout. */
export type SlideLayout =
  | "centered"
  | "event"
  | "welcome"
  | "scripture"
  /** The sermon title card: a photograph behind a centred, quoted title.
   *  Modelled on the one Cindi builds by hand in the announcements deck. */
  | "sermon";

// A type alias rather than an interface on purpose: only aliases get an
// implicit index signature, and this has to satisfy the API client's
// `data: Record<string, unknown>`.
export type TextSlideData = {
  /** Headline. On `scripture` this is the verse itself. */
  h?: string;
  /** Supporting line. */
  s?: string;
  /** Detail line — date/time/place on `event`, the reference on `scripture`. */
  m?: string;
  /** Small line above the headline, e.g. the church name. */
  eyebrow?: string;
  layout?: SlideLayout;
  bg?: string;
  fg?: string;
  /** Rules, eyebrow and detail line. Falls back to the LBC gold. */
  accent?: string;
  /** Set when something rewrites this slide on a schedule — currently the
   *  sermon job (`api/src/functions/sermon-slide.ts`), which finds its slide by
   *  this marker rather than by name so a rename doesn't orphan it. Carried
   *  through the editor untouched; losing it would make the next run create a
   *  duplicate instead of updating this row. */
  autoSource?: string;
  autoService?: string;
  /**
   * A picture behind the text, covering the slide.
   *
   * A path this app serves (`/sermon-slide-bg.jpg`), not a blob URL: blob reads
   * need a SAS that expires within the hour, and a slide that goes blank when
   * its signature lapses is worse than one with no picture. Static files are
   * served anonymously by the SWA and cached by the browser.
   */
  bgImage?: string;
  /** Black scrim over the picture, 0-1, so the text stays readable. */
  bgDim?: number;
  /** Small line in the top-right corner — the sermon date, on `sermon`. */
  corner?: string;
  /**
   * A QR code as a PNG data URI, drawn bottom-right on the `event` layout.
   *
   * Machine-set, not a field in the editor: the sermon job puts the code for
   * that sermon's SermonAudio page here once it has been preached. Deliberately
   * a finished picture rather than a URL — the matrix is generated once,
   * server-side, instead of by each of the three things that draw a slide.
   */
  qr?: string;
  /** The invitation above the code — "Watch or listen". */
  qrCaption?: string;
};

/** The stored shape plus the admin-only name. */
export interface TextSlide extends TextSlideData {
  name: string;
}

// Brand tokens, duplicated from index.css because these values are written into
// content records — a slide keeps the colour it was saved with, so it must be a
// literal here rather than a var() that a future restyle would silently change
// under already-published slides.
export const BRAND = {
  gold: "#a8854d",
  goldLight: "#d4b074",
  cream: "#fbf8f1",
  ink: "#2a2317",
  /** Dark sections are pure black to match the logo background. */
  black: "#000000",
  danger: "#7a1f1f",
} as const;

/** Defaults applied when a field is absent. `#0e1620` rather than BRAND.black
 *  is deliberate: it is what slides saved before templates rendered as, and
 *  changing a default would restyle existing content. */
export const SLIDE_FALLBACK = { bg: "#0e1620", fg: "#ffffff" } as const;

export interface SlideTemplate {
  id: string;
  label: string;
  /** Shown under the picker so a volunteer knows which to reach for. */
  hint: string;
  layout: SlideLayout;
  bg: string;
  fg: string;
  accent: string;
  /** Prefilled when the template is chosen and the field is still empty. */
  seed?: Partial<Pick<TextSlideData, "eyebrow">>;
}

/** Which fields each layout actually renders — the editor hides the rest, so a
 *  volunteer never types into a box that will not appear on the screen. */
export const LAYOUT_FIELDS: Record<
  SlideLayout,
  Array<"eyebrow" | "h" | "m" | "s">
> = {
  centered: ["h", "s"],
  event: ["eyebrow", "h", "m", "s"],
  sermon: ["eyebrow", "h", "m", "s"],
  welcome: ["eyebrow", "h", "s"],
  scripture: ["h", "m"],
};

/** Human labels for the fields, per layout — "Headline" means something
 *  different on a scripture slide than on an event one. */
export const FIELD_LABELS: Record<
  SlideLayout,
  Partial<Record<"eyebrow" | "h" | "m" | "s", { label: string; placeholder: string }>>
> = {
  centered: {
    h: { label: "Headline", placeholder: "Big text on the screen" },
    s: { label: "Subtext (optional)", placeholder: "" },
  },
  event: {
    eyebrow: { label: "Small line above", placeholder: "Liberty Bible Church" },
    h: { label: "Event name", placeholder: "Harvest Festival" },
    m: { label: "When and where", placeholder: "Saturday, October 18 · 5:30 PM · Fellowship Hall" },
    s: { label: "Extra detail (optional)", placeholder: "Free for all ages" },
  },
  welcome: {
    eyebrow: { label: "Small line above", placeholder: "Welcome to" },
    h: { label: "Headline", placeholder: "Liberty Bible Church" },
    s: { label: "Subtext (optional)", placeholder: "Gathering in the Word, in prayer, and in fellowship" },
  },
  scripture: {
    h: { label: "Verse", placeholder: "For God so loved the world…" },
    m: { label: "Reference", placeholder: "John 3:16" },
  },
  sermon: {
    eyebrow: { label: "Small line above", placeholder: "This Sunday's Sermon" },
    h: { label: "Sermon title", placeholder: "Who's the Boss?" },
    m: { label: "Passage", placeholder: "Genesis 9:1-17" },
    s: { label: "Date and speaker", placeholder: "Sunday, September 20 · Tom Zobrist" },
  },
};

export const TEMPLATES: SlideTemplate[] = [
  {
    id: "event",
    label: "Event",
    hint: "Name, date and place, left-aligned on black",
    layout: "event",
    bg: BRAND.black,
    fg: BRAND.cream,
    accent: BRAND.goldLight,
    seed: { eyebrow: "Liberty Bible Church" },
  },
  {
    id: "welcome",
    label: "Welcome",
    hint: "Centred on cream, for greeting people",
    layout: "welcome",
    bg: BRAND.cream,
    fg: BRAND.ink,
    accent: BRAND.gold,
    seed: { eyebrow: "Welcome to" },
  },
  {
    id: "scripture",
    label: "Scripture",
    hint: "A verse with its reference",
    layout: "scripture",
    bg: BRAND.black,
    fg: BRAND.cream,
    accent: BRAND.goldLight,
  },
  {
    id: "night",
    label: "Night",
    hint: "Plain headline, gold on black",
    layout: "centered",
    bg: BRAND.black,
    fg: BRAND.goldLight,
    accent: BRAND.gold,
  },
  {
    id: "gold",
    label: "Gold",
    hint: "Plain headline, cream on gold",
    layout: "centered",
    bg: BRAND.gold,
    fg: BRAND.cream,
    accent: BRAND.cream,
  },
  {
    id: "alert",
    label: "Alert",
    hint: "For urgent notices only",
    layout: "centered",
    bg: BRAND.danger,
    fg: "#ffffff",
    accent: "#ffffff",
  },
];

/** Apply a template, keeping any text the volunteer has already typed. */
export function applyTemplate(slide: TextSlide, t: SlideTemplate): TextSlide {
  return {
    ...slide,
    layout: t.layout,
    bg: t.bg,
    fg: t.fg,
    accent: t.accent,
    eyebrow: slide.eyebrow?.trim() ? slide.eyebrow : (t.seed?.eyebrow ?? ""),
  };
}

/** Strip fields the chosen layout does not render, so a slide's stored data
 *  matches what is actually on screen. */
export function forStorage(slide: TextSlide): TextSlideData {
  const layout = slide.layout ?? "centered";
  const shown = new Set(LAYOUT_FIELDS[layout]);
  return {
    layout,
    bg: slide.bg,
    fg: slide.fg,
    accent: slide.accent,
    h: shown.has("h") ? slide.h : undefined,
    s: shown.has("s") ? slide.s : undefined,
    m: shown.has("m") ? slide.m : undefined,
    eyebrow: shown.has("eyebrow") ? slide.eyebrow : undefined,
    // Not a rendered field, but it must survive a manual save — see the note on
    // the type. Dropping it here would silently orphan an auto-maintained slide.
    ...(slide.autoSource ? { autoSource: slide.autoSource } : {}),
    ...(slide.autoService ? { autoService: slide.autoService } : {}),
    // Machine-set decoration, kept for the same reason: none of it is a field
    // in the editor, so a volunteer opening the sermon slide and pressing Save
    // would otherwise strip the background and the QR off it until the next
    // cron run put them back.
    ...(slide.bgImage ? { bgImage: slide.bgImage } : {}),
    ...(slide.bgDim !== undefined ? { bgDim: slide.bgDim } : {}),
    ...(slide.corner ? { corner: slide.corner } : {}),
    ...(slide.qr ? { qr: slide.qr } : {}),
    ...(slide.qrCaption ? { qrCaption: slide.qrCaption } : {}),
  };
}

/** Human explanation of what keeps this slide up to date, or null if nothing
 *  does. Shown in the editor, because a slide that silently reverts overnight
 *  is a bad surprise. */
export function autoUpdateNote(slide: TextSlideData): string | null {
  if (slide.autoSource !== "sermon") return null;
  const which = slide.autoService === "p.m." ? "evening" : "morning";
  return `This slide updates itself each week from Pastor Tom's sermon notes (Sunday ${which}). Anything you change here will be overwritten the next time it runs — rename it first if you want to keep an edited copy.`;
}
